import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, FileText, Plane, ShieldCheck, Snowflake, Sun, Leaf, Grape } from "lucide-react";
import { MaskLines, Reveal, SectionHead } from "@/components/site/Motion";
import { CallbackSection } from "@/components/home/CallbackSection";
import { useEnquiry } from "@/context/EnquiryContext";
import { IMG } from "@/data/site";
import { RefusalStory } from "@/components/georgia/RefusalStory";
import { EImg, T } from "@/context/ContentContext";
import { useSeo } from "@/hooks/useSeo";

const VISA_ROUTES = [
  {
    icon: ShieldCheck, tag: "Route 01", title: "Visa-free entry with a valid third-country visa",
    text: "Indian passport holders with a valid visa or residence permit from the USA, UK, a Schengen country, Canada, Japan, Australia, New Zealand, South Korea, Israel or a GCC country can usually enter Georgia without a separate visa.",
    points: ["Stay up to 90 days in any 180-day period", "The qualifying visa or permit must be valid on the day you enter", "Physical visa stickers or residence cards are safest"],
  },
  {
    icon: FileText, tag: "Route 02", title: "Georgia e-Visa (online)",
    text: "No qualifying visa? Apply online on the official Government of Georgia portal, evisa.gov.ge. There is no visa on arrival for Indian citizens.",
    points: ["Single entry, stay of up to 30 days", "Government fee of about USD 20 plus a small bank charge", "Apply well ahead of your travel date"],
  },
];

const DOCS = [
  "Passport valid for at least 3 months beyond your stay",
  "Recent passport-size photo (digital, for e-Visa)",
  "Confirmed return or onward flight tickets",
  "Hotel bookings / trip itinerary",
  "Travel medical insurance for your full stay",
  "Recent bank statement as proof of funds",
];

const SEASONS = [
  { icon: Leaf, name: "Spring", months: "Apr – Jun", for: "Green valleys & city walks",
    text: "Mild days, wildflowers in Kazbegi and pleasant Tbilisi evenings. A great all-round time with fewer crowds.", tone: "bg-[#E7EFD9]" },
  { icon: Sun, name: "Summer", months: "Jul – Aug", for: "Beaches & mountain hikes",
    text: "Peak season. Black Sea days in Batumi and high-altitude hikes around Gudauri and Kazbegi. Book early.", tone: "bg-[#F8E3C4]" },
  { icon: Grape, name: "Autumn", months: "Sep – Nov", for: "Wine harvest & golden colours",
    text: "Rtveli, the grape harvest, fills Kakheti's cellars. Warm days, cool nights and the hills turn amber. Our favourite season.", tone: "bg-[#F1D2C4]", pick: true },
  { icon: Snowflake, name: "Winter", months: "Dec – Mar", for: "Snow, skiing & New Year",
    text: "Gudauri becomes a ski resort and Tbilisi lights up for the holidays. Some mountain roads may close after heavy snow.", tone: "bg-[#DCE4EC]" },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const GOOD = { Apr: 2, May: 3, Jun: 3, Jul: 2, Aug: 2, Sep: 3, Oct: 3, Nov: 1, Dec: 2, Jan: 2, Feb: 2, Mar: 1 };
const LABEL = { 1: "Quiet", 2: "Good", 3: "Best" };

function GuideHero() {
  return (
    <section className="relative bg-wine-deep text-cream overflow-hidden grain pt-36 pb-20 sm:pt-44 sm:pb-28" data-testid="visa-guide-hero">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <T k="guide.hero.eyebrow" as="p" className="eyebrow text-gold mb-6">Georgia guide · For Indian travellers</T>
          <h1 className="font-display font-extrabold tracking-[-0.045em] leading-[0.92] text-5xl sm:text-7xl lg:text-[5.5rem]">
            <MaskLines lines={[<T k="guide.hero.title.0">Visa, seasons</T>, <T k="guide.hero.title.1" className="font-serif italic font-normal text-gold">& the right time to go.</T>]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 max-w-lg text-lg text-cream/80">
            <T k="guide.hero.intro">Everything you need before booking Georgia, in one place. Still unsure? Our team handles the paperwork with you.</T>
          </motion.p>
        </div>
        <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.3, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-5 arch overflow-hidden aspect-[4/5] max-h-[70vh] w-full max-w-sm mx-auto lg:ml-auto">
          <EImg place="bottom-4 right-4" k="guide.hero.img" src={IMG.tbilisiRiver} alt="Old Tbilisi at dusk" className="h-full w-full object-cover" />
        </motion.div>
      </div>
    </section>
  );
}

function VisaSection() {
  const { openEnquiry } = useEnquiry();
  return (
    <section className="bg-paper py-24 sm:py-32" data-testid="visa-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead k="guide.visa" eyebrow="Part 01 · Visa" title={["Two ways in.", "Pick yours."]} />
        <div className="grid lg:grid-cols-2 gap-6">
          {VISA_ROUTES.map(({ icon: Icon, tag, title, text, points }, i) => (
            <Reveal key={tag} delay={i * 0.1} className="rounded-[28px] bg-cream p-7 sm:p-10 flex flex-col" data-testid={`visa-route-${i + 1}`}>
              <div className="flex items-center justify-between">
                <T k={`guide.route.${i}.tag`} className="eyebrow text-terra">{tag}</T>
                <Icon className="h-7 w-7 text-terra" />
              </div>
              <T k={`guide.route.${i}.title`} as="h3" className="mt-6 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</T>
              <T k={`guide.route.${i}.text`} as="p" className="mt-4 text-ink/80 leading-relaxed">{text}</T>
              <ul className="mt-6 space-y-3">
                {points.map((p, j) => <li key={j} className="flex gap-3"><Check className="h-5 w-5 text-terra shrink-0 mt-0.5" /><T k={`guide.route.${i}.point.${j}`}>{p}</T></li>)}
              </ul>
            </Reveal>
          ))}
        </div>
        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-8 rounded-[28px] border border-ink/10 p-7 sm:p-10" data-testid="visa-documents">
            <T k="guide.docs.title" as="p" className="eyebrow text-terra mb-5">Documents checklist</T>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {DOCS.map((d, j) => <li key={j} className="flex gap-3"><FileText className="h-5 w-5 text-stone-600 shrink-0 mt-0.5" /><T k={`guide.docs.${j}`}>{d}</T></li>)}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 rounded-[28px] bg-ink text-cream p-7 sm:p-10 flex flex-col justify-between gap-8">
            <div>
              <Plane className="h-7 w-7 text-gold" />
              <T k="guide.help.title" as="p" className="mt-5 font-display text-2xl font-bold">Want us to handle it?</T>
              <T k="guide.help.text" as="p" className="mt-2 text-cream/75">We guide every Georgia traveller through the visa and insurance paperwork.</T>
            </div>
            <button onClick={() => openEnquiry({ destination: "Georgia", trip_title: "Georgia visa help" })} data-testid="visa-help-btn"
              className="group inline-flex items-center justify-between rounded-full bg-terra pl-6 pr-2 py-2 font-semibold hover:bg-[#a5370a] transition-colors">
              <T k="guide.help.cta">Get visa help</T>
              <span className="grid place-items-center h-10 w-10 rounded-full bg-paper text-terra transition-transform group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
            </button>
          </Reveal>
        </div>
        <p className="mt-8 text-sm text-stone-600 max-w-3xl" data-testid="visa-disclaimer">
          <T k="guide.disclaimer">Georgia also requires travel medical insurance for foreign visitors. Visa and entry rules can change at short notice, so always confirm the latest requirements on the official portal</T> <a href="https://www.evisa.gov.ge" target="_blank" rel="noreferrer" className="underline hover:text-terra">evisa.gov.ge</a> <T k="guide.disclaimer2">or with our team before you book flights.</T>
        </p>
      </div>
    </section>
  );
}

function SeasonsSection() {
  return (
    <section className="bg-cream py-24 sm:py-32" data-testid="seasons-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead k="guide.seasons" eyebrow="Part 02 · Best time to visit" title={["Every season", "has a reason."]} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SEASONS.map(({ icon: Icon, name, months, for: f, text, tone, pick }, i) => (
            <Reveal key={name} delay={i * 0.08} className={`relative rounded-[28px] ${tone} p-7 flex flex-col min-h-[340px]`} data-testid={`season-${name.toLowerCase()}`}>
              {pick && <T k="guide.season.pick" className="absolute top-5 right-5 eyebrow bg-ink text-cream rounded-full px-3 py-1">Our pick</T>}
              <Icon className="h-8 w-8 text-ink" />
              <T k={`guide.season.${i}.months`} as="p" className="mt-8 font-mono text-sm text-ink/70">{months}</T>
              <T k={`guide.season.${i}.name`} as="h3" className="font-display text-4xl font-extrabold tracking-tight">{name}</T>
              <T k={`guide.season.${i}.for`} as="p" className="mt-1 font-serif italic text-xl text-ink/80">{f}</T>
              <T k={`guide.season.${i}.text`} as="p" className="mt-4 text-ink/80 leading-relaxed">{text}</T>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 rounded-[28px] bg-paper p-6 sm:p-8" data-testid="month-strip">
          <T k="guide.months.title" as="p" className="eyebrow text-terra mb-5">Month by month</T>
          <div className="grid grid-cols-6 sm:grid-cols-12 gap-2">
            {MONTHS.map((m) => (
              <div key={m} className="text-center">
                <div className="h-24 rounded-2xl bg-cream flex items-end overflow-hidden">
                  <motion.div initial={{ height: 0 }} whileInView={{ height: `${GOOD[m] * 33}%` }} viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className={`w-full ${GOOD[m] === 3 ? "bg-terra" : GOOD[m] === 2 ? "bg-gold" : "bg-stone-400"}`} />
                </div>
                <p className="mt-2 font-mono text-xs">{m}</p>
                <p className="text-[12px] text-stone-600">{LABEL[GOOD[m]]}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Link to="/georgia" data-testid="visa-guide-trips-link"
          className="group mt-12 inline-flex items-center gap-3 rounded-full border border-ink/20 px-6 py-3 font-semibold hover:bg-ink hover:text-cream transition-colors">
          <T k="guide.trips-link">See Georgia departures</T> <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
        </Link>
      </div>
    </section>
  );
}

export default function GeorgiaGuide() {
  useSeo({
    title: "Georgia e-Visa for Indians: Visa Guide & Best Time to Visit",
    description: "Georgia e-visa and visa guide for Indian travellers: the two ways in, documents checklist and the best season to visit. Get Georgia visa help from Trippy Tastic, Delhi.",
    path: "/georgia/visa-guide",
  });
  return (
    <main data-testid="georgia-guide-page">
      <GuideHero />
      <VisaSection />
      <RefusalStory />
      <SeasonsSection />
      <CallbackSection k="guide.callback" preset={{ destination: "Georgia" }} title={["Ready for", "Georgia?"]} />
    </main>
  );
}
